/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { Global, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useRef, useState } from 'react';

import { Divider, Grid, Typography2, useTheme } from '@coursera/cds-core';
import { ChevronDownIcon, ChevronUpIcon, CloseIcon, ErrorIcon } from '@coursera/cds-icons';

import Portal from 'bundles/employability-common/components/Portal';
import styles from 'bundles/employability-common/components/comboBoxStyles';
import useKeyboardNavList from 'bundles/employability-common/components/useKeyboardNavList';
import usePopoverState from 'bundles/employability-common/components/usePopoverState';

export type MultiSectionComboBoxItem = {
  key: string;
  match: (term?: string) => number;
  onClick: (closePopover?: () => void) => void;
  label?: string;
  content: (term?: string, matches?: number) => React.ReactNode;
};

export type ComboBoxProps = {
  items: { [sectionTitle: string]: Array<MultiSectionComboBoxItem> };
  onSearch?: (term: string, results: Array<MultiSectionComboBoxItem>) => void; // for dynamic items
  activePlaceholder?: string;
  inactivePlaceholder?: React.ReactNode;
  footer?: (close: () => void) => React.ReactNode;
  header?: (close: () => void) => React.ReactNode;
  totalCount?: number; // override for the total records count
  fullWidth?: boolean;
  width?: string; // css value for width. discarded if fullWidth === true
  errorMessage?: string;
  'data-e2e'?: string;
};

type DisplayedItemsState = {
  [sectionTitle: string]: {
    items: Array<MultiSectionComboBoxItem & { matches: number }>;
    displayedCount: number;
    totalCount: number;
    termUsed?: string;
  };
};

const MultiSectionComboBox: React.FC<ComboBoxProps> = ({
  items: rawItems,
  onSearch,
  activePlaceholder,
  inactivePlaceholder,
  footer,
  header,
  totalCount: totalOverride,
  fullWidth,
  width,
  errorMessage,
  'data-e2e': e2e,
}) => {
  const theme = useTheme();
  const inputElementRef = useRef<HTMLInputElement>(null);
  const [inputTerm, setTerm] = useState<string>('');

  const [termUsed, setTermUsed] = useState<string | undefined>(undefined);

  const mapRawItemsToDisplayed = React.useCallback<(delay: number) => DisplayedItemsState>(
    (delay) =>
      Object.keys(rawItems).reduce((acc, sectionTitle) => {
        const searchedItems = rawItems[sectionTitle]
          .map((i) => ({ ...i, matches: i.match(inputTerm) || 0 }))
          .filter((i) => (!inputTerm ? true : i.matches > 0))
          .sort((a, b) => b.matches - a.matches);

        // only trigger on search when the term changing caused this effect to trigger
        if (typeof onSearch === 'function' && delay) {
          onSearch(inputTerm, searchedItems);
        }

        // only keep the first 100 to keep the number of dom nodes reasonable. a more precise search term will need to be used for those excluded
        const limited = searchedItems.slice(0, 100);
        return {
          ...acc,
          [sectionTitle]: {
            totalCount: searchedItems.length,
            displayedCount: limited.length,
            items: limited,
          },
        };
      }, {}),
    [inputTerm, onSearch, rawItems]
  );
  const [displayedItems, setDisplayedItems] = useState<DisplayedItemsState>(mapRawItemsToDisplayed(0));

  const { isOpen, pos, closePopover, openPopover, anchorElementRef, containerElementRef } = usePopoverState();

  useEffect(() => {
    // filtering a large amount of items can be expensive so wait for the user to stop typing
    // bypass the debounce when the list itself changes for ui responsiveness
    const delay = termUsed !== inputTerm ? 200 : 0;
    const delayDebounceFn = setTimeout(() => {
      const searchTerm = inputTerm;

      setTermUsed(searchTerm);
      setDisplayedItems(mapRawItemsToDisplayed(delay));
    }, delay);

    return () => clearTimeout(delayDebounceFn);
  }, [rawItems, termUsed, inputTerm, mapRawItemsToDisplayed]);

  const itemAction = (item?: MultiSectionComboBoxItem) => {
    if (item) {
      item.onClick(closePopover);
    }
  };

  const { selectedId, clearSelection, selectedElementRef } = useKeyboardNavList(
    Object.values(displayedItems).reduce((acc, val) => [...acc, ...val.items], [] as Array<MultiSectionComboBoxItem>),
    itemAction,
    isOpen
  );

  // clear state when popover visibility toggled
  useEffect(() => {
    if (isOpen) {
      inputElementRef.current?.focus();
    } else {
      setTerm('');
    }
  }, [isOpen]);

  return (
    <>
      {Boolean(errorMessage) && (
        <Grid
          css={styles.errorMessage}
          direction="row"
          wrap="wrap"
          alignItems="center"
          container
          data-testid="combo-box-error-message"
        >
          <ErrorIcon size="small" color="error" css={styles.warningIcon} />
          <Typography2 variant="subtitleMedium" color="error" component="span">
            {errorMessage}
          </Typography2>
        </Grid>
      )}
      <div css={styles.cont} className={'combobox-' + (isOpen ? 'open' : 'closed')} data-e2e={e2e}>
        <Global styles={styles.backdrop} />
        <button
          ref={anchorElementRef}
          css={styles.toggleButton(theme, fullWidth ? '100%' : width)}
          onClick={() => openPopover()}
          type="button"
          role="combobox"
        >
          {isOpen ? (
            <React.Fragment>
              <input css={styles.input} />
              {inputTerm.length ? (
                <CloseIcon color="default" data-testid="icon-clear" />
              ) : (
                <ChevronUpIcon color="default" data-testid="icon-active" />
              )}
            </React.Fragment>
          ) : (
            <React.Fragment>
              <Typography2 component="p" variant="bodyPrimary">
                {inactivePlaceholder}
              </Typography2>
              <ChevronDownIcon color="default" data-testid="icon-not-active" />
            </React.Fragment>
          )}
        </button>
        {isOpen && (
          <Portal>
            <div css={styles.portal(pos)} ref={containerElementRef}>
              <div css={styles.clickLayer(theme, fullWidth ? '100%' : width)}>
                <input
                  css={styles.input}
                  ref={inputElementRef}
                  placeholder={activePlaceholder}
                  onChange={(e) => {
                    setTerm(e.target.value);
                  }}
                  value={inputTerm}
                  onFocus={() => openPopover()}
                />
                {/* suppress lints because keyboard nav is already handled by useKeyboardNavList hook */}
                {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
                <div
                  css={styles.clearButton}
                  onClick={() => {
                    // reset the input if anything has been typed, if not close popover
                    if (inputTerm.length) {
                      // reset input
                      setTerm('');
                      inputElementRef.current?.focus();
                    } else {
                      closePopover();
                    }
                  }}
                />
              </div>
              <div css={styles.popover}>
                {header && header(closePopover)}
                <div css={styles.results} role="listbox">
                  {Object.entries(displayedItems).map(([sectionTitle, sectionStatus], idx) => {
                    const totalCount = typeof totalOverride !== 'undefined' ? totalOverride : sectionStatus.totalCount;
                    return (
                      <React.Fragment key={sectionTitle}>
                        {sectionTitle && (
                          <Typography2
                            component="h3"
                            variant="subtitleMedium"
                            css={styles.sectionHeaders}
                            data-testid="combo-box-section-header"
                          >
                            {sectionTitle}
                          </Typography2>
                        )}
                        {sectionStatus.displayedCount ? (
                          <React.Fragment>
                            {sectionStatus.items.map((item) => {
                              const selected = item.key === selectedId;

                              return (
                                // suppress lints because keyboard nav is already handled by useKeyboardNavList hook
                                // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/interactive-supports-focus
                                <div
                                  aria-selected={selected}
                                  ref={selected ? selectedElementRef : undefined}
                                  key={item.key}
                                  css={styles.item}
                                  className={selected ? 'menu-item-active' : 'menu-item-inactive'}
                                  role="option"
                                  onClick={() => {
                                    clearSelection();
                                    itemAction(item);
                                  }}
                                >
                                  {item.content(termUsed, item.matches)}
                                </div>
                              );
                            })}
                            {totalCount > sectionStatus.displayedCount && (
                              <div className="combobox-info">
                                <div>And {totalCount - sectionStatus.displayedCount} more...</div>
                              </div>
                            )}
                          </React.Fragment>
                        ) : (
                          <div className="combobox-info">No results</div>
                        )}
                        {sectionTitle && idx !== Object.keys(rawItems).length - 1 && (
                          <Divider css={styles.sectionDivider} data-testid="combo-box-section-divider" />
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
                {footer && footer(closePopover)}
              </div>
            </div>
          </Portal>
        )}
      </div>
    </>
  );
};

export default MultiSectionComboBox;
